// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Stats Screen
// Overview cards: transactions, pending sync, cash session, connection status
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { useAppStore } from '@/store/app-store';
import { useAuthStore } from '@/store/auth-store';
import { useCartStore } from '@/store/cart-store';
import { useCashStore } from '@/store/cash-store';
import { useConfigStore } from '@/store/config-store';
import { formatPrice } from '@/lib/price-formatter';
import { apiClient } from '@/services/api-client';
import type { TicketData } from '@/components/TicketCard';

interface StatsData {
  ticketCount: number;
  totalAmount: number;
  averageAmount: number;
  pendingSync: number;
}

export default function StatsScreen() {
  const isOnline = useAppStore((s) => s.isOnline);
  const user = useAuthStore((s) => s.user);
  const posName = useConfigStore((s) => s.posName);
  const cashSessionId = useCashStore((s) => s.cashSessionId);
  const sessionData = useCashStore((s) => s.sessionData);

  const [stats, setStats] = useState<StatsData>({
    ticketCount: 0,
    totalAmount: 0,
    averageAmount: 0,
    pendingSync: 0,
  });
  const [loading, setLoading] = useState(true);

  // Load today's stats
  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      setLoading(true);
      try {
        const response = await apiClient.getTodayTickets();
        const tickets = (response.transactions ?? []) as TicketData[];

        const total = tickets.reduce((sum, t) => sum + (t.total ?? t.total_amount ?? 0), 0);
        const count = tickets.length;

        if (!cancelled) {
          setStats({
            ticketCount: count,
            totalAmount: total,
            averageAmount: count > 0 ? total / count : 0,
            pendingSync: 0, // TODO: read from offlineQueue when available
          });
        }
      } catch {
        // Keep defaults if loading fails
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStats();
    return () => { cancelled = true; };
  }, []);

  const cards = [
    {
      label: "Tickets aujourd'hui",
      value: String(stats.ticketCount),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
      color: 'text-accent',
    },
    {
      label: 'Chiffre du jour',
      value: formatPrice(stats.totalAmount),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      color: 'text-success',
    },
    {
      label: 'Panier moyen',
      value: formatPrice(stats.averageAmount),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      color: 'text-info',
    },
    {
      label: 'En attente sync',
      value: String(stats.pendingSync),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
      color: stats.pendingSync > 0 ? 'text-warning' : 'text-gray-400',
    },
  ];

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="px-4 py-3">
        <h2 className="text-base font-semibold text-white">Statistiques</h2>
        <p className="text-gray-400 text-xs">
          {user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email : 'Utilisateur'} -- {posName}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 px-4 mb-4">
        {cards.map((card) => (
          <div key={card.label} className="card p-4">
            <div className={`mb-2 ${card.color}`}>{card.icon}</div>
            <p className={`text-xl font-bold ${card.color}`}>
              {loading ? '...' : card.value}
            </p>
            <p className="text-xs text-gray-400 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Session info */}
      <div className="mx-4 card p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-3">Session de caisse</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Session</span>
            <span className="text-white font-medium">
              {cashSessionId ? `#${cashSessionId}` : 'Aucune'}
            </span>
          </div>
          {sessionData?.opened_at && (
            <div className="flex justify-between">
              <span className="text-gray-400">Ouverte a</span>
              <span className="text-white font-medium">
                {new Date(sessionData.opened_at).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Connection status */}
      <div className="mx-4 card p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-3">Connexion</h3>
        <div className="flex items-center gap-3">
          <div
            className={`w-3 h-3 rounded-full ${isOnline ? 'bg-success' : 'bg-danger'}`}
          />
          <span className="text-sm text-gray-300">
            {isOnline ? 'Connecte au serveur' : 'Hors ligne'}
          </span>
        </div>
        {!isOnline && (
          <p className="text-xs text-gray-500 mt-2">
            Les transactions sont stockees localement et seront synchronisees automatiquement.
          </p>
        )}
      </div>
    </div>
  );
}
